/**
 * @fileoverview A component that provides a phone input field with country code selection.
 * It uses the `libphonenumber-js` library to format the phone number.
 *
 * @example
 * <PhoneInput onChange={handleChange} id="phone" countryCode="US" />
 */

import { AsYouType, getCountryCallingCode } from 'libphonenumber-js'
import { ChangeEvent, useEffect, useState } from 'react'
import GBFlagIcon from '../assets/flags/gb.svg'
import USFlagIcon from '../assets/flags/us.svg'


/**
 * @interface PhoneInputProps
 * @description Props for the PhoneInput component.
 * @property {(value: string) => void} onChange - The function to call when the phone number is changed.
 * @property {string} id - The ID of the input field.
 * @property {'US' | 'GB'} countryCode - The country code for the phone number.
 * @property {string} error - The error message to display if the phone number is invalid.
 * @property {string} value - The value of the phone number.
 */
interface PhoneInputProps {
    onChange: (value: string) => void
    id: string
    countryCode: 'US' | 'GB'
    error?: string
    value?: string
}

/**
 * @function classNames
 * @description Utility function to join class names with spaces.
 * @param {...string} classes - The class names to join.
 * @returns {string} - The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function getCountryIcon
 * @description Returns the appropriate flag icon for the given country code.
 * @param {string} countryCode - The country code.
 * @returns {JSX.Element | null} - The flag icon element.
 */
function getCountryIcon(countryCode: string) {
    switch (countryCode) {
        case 'US':
            return <USFlagIcon className="w-6 h-6" />
        case 'GB':
            return <GBFlagIcon className="w-6 h-6" />
        default:
            return null
    }
}

/**
 * @function PhoneInput
 * @description Component for providing a phone input field with country code selection.
 * @param {PhoneInputProps} props - The props for the PhoneInput component.
 * @returns {JSX.Element | null} - The PhoneInput component.
 */
export function PhoneInputComponent({
    onChange,
    countryCode = 'US',
    id,
    value,
    error,
}: PhoneInputProps) {
    const [formattedValue, setFormattedValue] = useState(value)

    useEffect(() => {
        setFormattedValue(value)
    }, [value])

    const formatPhoneNumber = (input: string) => {
        const asYouType = new AsYouType(countryCode)
        const countryCallingCode = getCountryCallingCode(countryCode)

        // If input starts with +, assume it is an international number
        // And pass it directly to the input method
        if (input.startsWith('+')) {
            asYouType.input(input)
        }
        // If input starts with the country calling code
        // Add the '+' prefix and pass it to the input method
        else if (input.startsWith(countryCallingCode)) {
            asYouType.input(`+${input}`)
        }
        // For other cases, manually prepend the '+' and country calling code
        // before using the input method
        else {
            asYouType.input(`+${countryCallingCode}${input}`)
        }

        const phoneNumber = asYouType.getNumber()
        return phoneNumber ? phoneNumber.formatInternational() : input
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value
        if (rawValue.length > 15) return

        const onlyNumbers = rawValue.replace(/[^+0-9\s()-]*$/g, '')
        const formatted = formatPhoneNumber(onlyNumbers)
        setFormattedValue(formatted)
        onChange(formatted)
    }

    return (
        <>
            <div className="h-10 flex flex-row items-center rounded-md shadow-sm">
                <div
                    className={classNames(
                        'py-2 px-3 h-10 bg-gray-100 border border-r-0 rounded-l-md',
                        error ? 'border-red-300' : 'border-gray-300'
                    )}
                >
                    {getCountryIcon(countryCode)}
                </div>
                <input
                    type="tel"
                    id={id}
                    name={id}
                    className={classNames(
                        'h-10 w-full font-normal text-sm text-gray-900 focus:ring-indigo-500 rounded-r-md',
                        error ? 'border-red-300' : 'border-gray-300'
                    )}
                    value={formattedValue || ''}
                    onChange={handleInputChange}
                    placeholder={`+${getCountryCallingCode(countryCode)}`}
                />
            </div>
            {error ? <p className="pt-1 text-xs text-red-600">{error}</p> : null}
        </>
    )
}
